/**
 * WaterPanel — provincial reservoir context for Nakhon Ratchasima.
 * Data: data.go.th ปริมาณน้ำในอ่างเก็บน้ำภายในจังหวัด (โครงการชลประทานนครราชสีมา).
 *
 * This is the ANNUAL provincial picture — all 29 RID reservoirs, published
 * once a year. It is deliberately distinct from the FloodRiskPanel above,
 * which carries today's Lam Takhong telemetry from ThaiWater. The two will
 * disagree on Lam Takhong's volume, and that is correct: one is a yearly
 * snapshot, the other is this morning's reading. The header states the
 * Buddhist-era year so nobody mistakes it for live data.
 *
 * The dataset publishes stored volume and usable volume only — no design
 * capacity, supply-days, or rainfall. Those render as "—" rather than being
 * invented. The bar shows usable share of stored water, which is the
 * meaningful ratio this dataset supports.
 */

import { PanelHeader } from './PanelHeader';
import { Arrow } from './Arrow';

export interface ReservoirStatus {
  name: string;
  district: string;
  capacityPct: number | null;
  currentVolMCM: number | null;
  usableVolMCM: number | null;
  usablePct: number | null;
  maxVolMCM: number | null;
  daysRemaining: number | null;
  rainfallYesterdayMm: number | null;
  trend: "rising" | "falling" | "stable";
  year: number;
}

interface Props {
  data: ReservoirStatus[];
  loading: boolean;
  ageMinutes?: number;
}

// Usable share thresholds. Below 30 % of stored water being deliverable means
// most of what's left is dead storage — the operative drought signal here.
const USABLE_LOW = 30;
const USABLE_WATCH = 50;
/** Reservoirs shown before the "+N more" line — keeps the rail scannable. */
const VISIBLE_ROWS = 8;

function usableColor(pct: number | null): string {
  if (pct == null) return "var(--data)";
  if (pct < USABLE_LOW) return "var(--bad)";
  if (pct < USABLE_WATCH) return "var(--warn)";
  return "var(--good)";
}

function Bar({ pct }: { pct: number | null }) {
  const w = Math.max(0, Math.min(100, pct ?? 0));
  return (
    <div style={{ height: 4, background: "var(--line)", position: "relative", marginTop: 2 }}>
      <div style={{ position: "absolute", left: 0, top: 0, height: "100%", width: `${w}%`, background: usableColor(pct) }} />
    </div>
  );
}

const TREND_GLYPH = {
  rising:  <Arrow direction="up"   aria-label="rising" />,
  falling: <Arrow direction="down" aria-label="falling" />,
  stable:  <span aria-label="stable">—</span>,
} as const;

export function WaterPanel({ data, loading, ageMinutes }: Props) {
  if (loading && data.length === 0) {
    return (
      <div className="col">
        <div className="eyebrow">RESERVOIRS // NAKHON RATCHASIMA</div>
        <div className="skeleton" style={{ height: 12, marginTop: 8 }} />
        <div className="skeleton" style={{ height: 12, marginTop: 6, width: "80%" }} />
      </div>
    );
  }
  if (!data.length) return null;

  const year = data[0].year;
  const totalStored = data.reduce((s, r) => s + (r.currentVolMCM ?? 0), 0);
  const totalUsable = data.reduce((s, r) => s + (r.usableVolMCM ?? 0), 0);
  const provinceUsablePct = totalStored > 0 ? Math.round((totalUsable / totalStored) * 100) : null;
  const falling = data.filter((r) => r.trend === "falling").length;
  const visible = data.slice(0, VISIBLE_ROWS);

  return (
    <div className="col" style={{ gap: 6 }}>
      <PanelHeader
        title="อ่างเก็บน้ำจังหวัด "
        ageMinutes={ageMinutes}
        fallbackTier="database"
        source="data.go.th·rid"
      />

      <div className="caption" style={{ color: "var(--text-3)" }}>
        สรุปรายปี พ.ศ. {year} · {data.length} อ่างทั้งจังหวัด — ตัวเลขลำตะคองแบบเรียลไทม์อยู่แผงด้านบน
      </div>

      {/* Province aggregate — usable share of everything stored */}
      <div>
        <div className="spread" style={{ alignItems: "center" }}>
          <div className="eyebrow">น้ำใช้การได้ทั้งจังหวัด</div>
          <div className="eyebrow mono" style={{ color: usableColor(provinceUsablePct) }}>
            {provinceUsablePct != null ? `${provinceUsablePct}%` : "—"}
          </div>
        </div>
        <Bar pct={provinceUsablePct} />
        <div className="eyebrow mono" style={{ color: "var(--text-3)" }}>
          {totalUsable.toFixed(1)} / {totalStored.toFixed(1)} ล้าน ม.³
          {falling > 0 ? ` · ลดลง ${falling} อ่าง` : ""}
        </div>
      </div>

      {/* Per-reservoir rows, largest first */}
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {visible.map((r) => {
          const shortName = r.name.replace(/^อ่างฯ|^อ่างเก็บน้ำ/, "").trim();
          return (
            <div key={r.name} style={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <div className="spread" style={{ alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <span
                    style={{
                      color: r.trend === "falling" ? "var(--warn)" : r.trend === "rising" ? "var(--good)" : "var(--text-3)",
                      fontFamily: "var(--font-mono)",
                      fontSize: "var(--size-eyebrow)",
                    }}
                  >
                    {TREND_GLYPH[r.trend]}
                  </span>
                  <span style={{ fontSize: "var(--size-eyebrow)" }}>{shortName}</span>
                  <span className="caption" style={{ color: "var(--text-3)" }}>{r.district}</span>
                </div>
                <div className="eyebrow mono" style={{ color: usableColor(r.usablePct) }}>
                  {r.currentVolMCM != null ? `${r.currentVolMCM.toFixed(1)}` : "—"}
                  {r.usablePct != null ? ` · ใช้ได้ ${r.usablePct.toFixed(0)}%` : ""}
                </div>
              </div>
              <Bar pct={r.usablePct} />
            </div>
          );
        })}
        {data.length > VISIBLE_ROWS && (
          <div className="caption" style={{ color: "var(--text-3)" }}>
            + อีก {data.length - VISIBLE_ROWS} อ่าง
          </div>
        )}
      </div>

      <div className="eyebrow mono" style={{ color: "var(--text-3)" }}>
        SOURCE · DATA.GO.TH · โครงการชลประทานนครราชสีมา
      </div>
    </div>
  );
}
